import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';

/**
 * Mapa de poltronas de uma viagem de ônibus (`bus_seat_map`) — INTERATIVO. Mostra
 * o(s) trecho(s) (IDA · VOLTA) com as fileiras de poltronas; o cliente toca numa
 * poltrona LIVRE de CADA trecho e, no fim, um único **Continuar** devolve a escolha
 * COMBINADA por `interaction` (challengeId + `BusSeatSelection` opaco), exatamente
 * como o painel de tarifas do `flight_offers` resolve as duas pernas num card só. O
 * `seatCode` NUNCA transita como texto livre do LLM — viaja estruturado pelo canal
 * de confirmação (anti-injection).
 *
 * One-way: um único trecho (`legs` de 1 entrada). Round-trip: dois trechos (IDA +
 * VOLTA) no MESMO card, resolvidos juntos numa só interação.
 */

/** Uma poltrona individual do mapa. */
const ChatUiBusSeatSchema = z.object({
  // Código opaco da poltrona (ex.: "12A") — volta ESTRUTURADO na seleção.
  code: z.string().min(1),
  // Ocupada → não-clicável (cinza).
  occupied: z.boolean(),
  // Coluna REAL da poltrona na fileira (1..5, cru do upstream; o corredor
  // `code:"0"` — coluna 3 no 2+2 — já foi removido). Dirige a PLANTA do ônibus:
  // 1/2 à esquerda, 4/5 à direita, corredor no gap entre as colunas 2 e 4; 2+1
  // (leito-cama) cai das colunas presentes. Ausente → poltrona sequencial (fallback).
  column: z.number().int().min(1).max(5).optional(),
});
export type ChatUiBusSeat = z.infer<typeof ChatUiBusSeatSchema>;

/** Uma fileira de poltronas (numeração crescente do embarque ao fundo). */
const ChatUiBusSeatRowSchema = z.object({
  rowNumber: z.number().int().min(1),
  seats: z.array(ChatUiBusSeatSchema).min(1),
});
export type ChatUiBusSeatRow = z.infer<typeof ChatUiBusSeatRowSchema>;

/** Um andar do ônibus (ônibus leito de dois andares → 2 andares por trecho). */
const ChatUiBusFloorSchema = z.object({
  rows: z.array(ChatUiBusSeatRowSchema).min(1),
});
export type ChatUiBusFloor = z.infer<typeof ChatUiBusFloorSchema>;

/** Contexto da viagem cujas poltronas estão sendo escolhidas (display). */
const ChatUiBusSeatTripSchema = z.object({
  carrierName: z.string().min(1),
  from: z.string().min(1),
  to: z.string().min(1),
});
export type ChatUiBusSeatTrip = z.infer<typeof ChatUiBusSeatTripSchema>;

/**
 * Um TRECHO do card combinado (IDA ou VOLTA). Carrega a grade daquele trecho OU o
 * estado de erro `seatError` SÓ para aquele trecho (o outro trecho segue com sua
 * grade). No estado de erro (`seatError: true`) a consulta de assentos daquele
 * trecho falhou (recorrente na VOLTA): a seção NÃO mostra a grade — mostra a
 * mensagem + "seguir sem assento" para AQUELE trecho; o `Continuar` do card só
 * habilita quando CADA trecho tem uma resolução (poltrona OU sem assento).
 */
const ChatUiBusSeatLegSchema = z
  .object({
    leg: z.enum(['outbound', 'return']),
    trip: ChatUiBusSeatTripSchema,
    // Vazio quando `seatError` (a grade não carregou). `.min(1)` só quando NÃO há
    // erro (grade normal) — imposto pelo refine abaixo.
    floors: z.array(ChatUiBusFloorSchema),
    // `true` ⇒ a consulta de assentos deste trecho falhou; a seção mostra a
    // mensagem + "seguir sem assento" no lugar da grade.
    seatError: z.boolean().optional(),
  })
  .strict()
  .refine((l) => l.seatError === true || l.floors.length >= 1, {
    message: 'floors must be non-empty unless seatError is set',
    path: ['floors'],
  });
export type ChatUiBusSeatLeg = z.infer<typeof ChatUiBusSeatLegSchema>;

/** Membro `bus_seat_map` do `ChatUiSchema` (discriminatedUnion). */
export const BusSeatMapBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('bus_seat_map'),
    // Challenge da seleção COMBINADA (anti-injection; congela o card ao resolver).
    // UM só challenge para os dois trechos.
    challengeId: z.string().min(1),
    // 1 trecho (one-way) ou 2 (round-trip: IDA + VOLTA) no MESMO card.
    legs: z.array(ChatUiBusSeatLegSchema).min(1).max(2),
  })
  .strict();
export type BusSeatMapBlock = z.infer<typeof BusSeatMapBlockSchema>;

/**
 * Resolução de UM trecho, dentro do payload combinado da seleção. Ou uma POLTRONA
 * (`seatCode`, código opaco que o card ofereceu — o LLM nunca o inventa,
 * anti-injection) OU "seguir sem assento" (`noSeat: true` — a equipe VOLL escolhe;
 * o booking OMITE travelSeats desse trecho). Exatamente um dos dois.
 */
export const BusSeatLegSelectionSchema = z.union([
  z.object({ seatCode: z.string().min(1) }).strict(),
  z.object({ noSeat: z.literal(true) }).strict(),
]);
// Discriminantes explícitos (`noSeat?: never` no ramo com poltrona e vice-versa)
// para o acesso `sel.noSeat` estreitar sem cast — o `z.infer` da union crua não os
// expõe. `never` (em vez de `undefined`) evita o par redundante `?`+`undefined`
// (S4782) mantendo a mesma forma opcional-e-ausente.
export type BusSeatLegSelection =
  | { seatCode: string; noSeat?: never }
  | { seatCode?: never; noSeat: true };

/**
 * Escolha COMBINADA do cliente, ecoada em `AppChannelInboundRequest.confirmation.
 * selections` (+ challengeId). O card devolve a resolução de CADA trecho num payload
 * só: `outbound` sempre presente; `return` só em round-trip. Um único `Continuar`
 * dispara esta seleção — o handler persiste AMBOS os trechos no estado per-leg.
 */
export const BusSeatSelectionSchema = z
  .object({
    seats: z
      .object({
        outbound: BusSeatLegSelectionSchema,
        return: BusSeatLegSelectionSchema.optional(),
      })
      .strict(),
  })
  .strict();
export type BusSeatSelection = z.infer<typeof BusSeatSelectionSchema>;
